import { useEffect, useMemo, useState } from 'react';
import { LikelyToFillIndicator } from '~/constants/ff.posthog';
import { useFeatureFlagVariant } from '~/hooks/useFeatureFlagVariant';
import { usePosthog } from '~/hooks/usePosthog';
import { parseLikelyToFillBands, type TLikelyToFillBands } from '../fields/likelyToFillConfig';

interface IUseLikelyToFillExperimentResult {
  /** Variant check: a boolean flag read is true for both arms. */
  isTreatment: boolean;
  bands: TLikelyToFillBands;
}

// posthog-js parses the payload into a new object on every read, so it's kept as a string: an
// unchanged payload then skips the re-render and keeps the same `bands` reference.
const readPayload = (posthogService: ReturnType<typeof usePosthog> | null): string | null => {
  const payload: unknown = posthogService?.getFeatureFlagPayload(LikelyToFillIndicator);
  if (payload == null) return null;
  return typeof payload === 'string' ? payload : JSON.stringify(payload);
};

/** Arm and bands of the Likely to Fill A/B test (GOTU-13065). */
export const useLikelyToFillExperiment = (): IUseLikelyToFillExperimentResult => {
  const posthogService = usePosthog();
  const isTreatment = useFeatureFlagVariant(LikelyToFillIndicator, 'treatment');

  const [payload, setPayload] = useState(() => readPayload(posthogService));

  useEffect(() => {
    if (!posthogService) return undefined;

    // The PosthogService wrapper drops the SDK's unsubscribe, so ignore late callbacks after unmount.
    let active = true;
    const update = () => {
      if (active) setPayload(readPayload(posthogService));
    };

    update();
    posthogService.onFeatureFlags(update);

    return () => {
      active = false;
    };
  }, [posthogService]);

  const bands = useMemo(() => parseLikelyToFillBands(payload), [payload]);

  return { isTreatment, bands };
};
